
import { defineComponent, toRaw, ref } from 'vue'
import CheckoutService from '@/services/CheckoutService'
import StripeCardElement from './StripeCardElement.vue'
import { currency } from '@/helper/constants'
import { UpsellOfferType } from '@/models/UpsellOffer'
import { isClientPortal } from '@/helper'
import PaymentV2 from './PaymentV2.vue'
import { v4 as uuidv4 } from 'uuid'
import {
  UIModal,
  UIModalHeader,
  UIButton,
  UITextSmRegular,
  UITextSmMedium,
  UIDisplaySmMedium,
  UITextXsMedium,
} from '@gohighlevel/ghl-ui'
import {
  CreditCard01Icon,
  CheckCircleIcon,
  Ticket02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import CouponFormModal from '@/components/checkout/CouponFormModal.vue'

const childRef = ref(null)

export default defineComponent({
  components: {
    StripeCardElement,
    PaymentV2,
    UIModal,
    UIModalHeader,
    UIButton,
    UITextSmRegular,
    UITextSmMedium,
    Ticket02Icon,
    UIDisplaySmMedium,
    UITextXsMedium,
    CouponFormModal,
  },
  name: 'PaymentModal',
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    location: Object,
    contact: Object,
    locationId: String,
    offerId: String,
    checkoutSource: String,
    showThankYouMsg: Boolean,
    freeProduct: Boolean,
    offer: Object,
    isUpsell: {
      type: Boolean,
      default: false,
    },
    upsellType: {
      type: String,
      default: null,
    },
    paymentMethods: {
      type: Array,
      default: null,
    },
    icon: {
      type: Function,
      default: CreditCard01Icon,
    },
    checkIcon: {
      type: Function,
      default: CheckCircleIcon,
    },
    showCoupon: {
      type: Boolean,
      default: false,
    },
    couponData: {
      type: Object,
      default: null,
    },
  },
  data() {
    currency
    return {
      currency,
      stripeAPIToken: '',
      stripe: {} as any,
      stripeElements: {} as any,
      elements: {} as any,
      card: {} as any,
      errMsg: '',
      disableCheckoutBtn: false,
      openModal: false,
      chargeCardType: 'newCard',
      selectedSavedCard: '',
      loadingPayButton: false,
      localCouponData: null,
      isCouponModalActive: false,
      appliedCouponData: null,
      updateKey: uuidv4(),
    }
  },
  computed: {
    couponsFeatureFlag() {
      return this.$store.state.featureFlag?.coupons
    },
    isInAppUpsell(): boolean {
      return this.upsellType ? this.upsellType === UpsellOfferType.INAPP : false
    },
    offerVersion(): string {
      return this.offer && this.offer.paymentPriceId ? 'v2' : 'v1'
    },
    isLivePayment(): boolean {
      return toRaw(this.offer).isLivePaymentMode || false
    },
    dashboardBtn(): string {
      return isClientPortal() ? 'Login' : 'View Dashboard'
    },
    modalTitleAndDescription(): { title: string; description: string } {
      const thankYouMessageTitle: string = this.freeProduct
        ? 'Thank you for signing up!'
        : 'Thank you for your purchase!'
      return {
        title: this.showThankYouMsg ? thankYouMessageTitle : '',
        description: this.showThankYouMsg
          ? 'We have sent you an email with login credentials'
          : '',
      }
    },
  },
  async mounted() {
    this.stripeAPIToken = this.location.stripe
    this.openModal = this.showModal
    if (this.paymentMethods?.length) {
      this.chargeCardType = 'savedCards'
    }
    this.localCouponData = this.couponData
  },
  methods: {
    fetchCurrencySymbol(currency: string) {
      if (currency) {
        return this.currency[currency].symbol
      }
      return ''
    },
    toggleCouponModal() {
      this.isCouponModalActive = !this.isCouponModalActive
      if (this.localCouponData && !this.localCouponData.isValidCode) {
        this.appliedCouponData = null
      }
      this.updateKey = uuidv4()
    },
    updateCouponData(data: any) {
      this.localCouponData = data
      this.appliedCouponData = data
    },
    async onConfirmPayment() {
      this.loadingPayButton = true
      if (this.showThankYouMsg) {
        this.$emit('go-to-preview')
      } else {
        if (this.offerVersion === 'v1') {
          await this.purchase()
        } else {
          await this.inititeOrder()
        }
      }
      this.loadingPayButton = false
    },
    async inititeOrder() {
      await this.$refs.childRef.processPayment()
    },
    getCardIcon(type: string) {
      switch (type) {
        case 'amex':
          return require('@/assets/cards/amex.svg')
        case 'diners':
          return require('@/assets/cards/diners.svg')
        case 'discover':
          return require('@/assets/cards/discover.svg')
        case 'mastercard':
          return require('@/assets/cards/mastercard.svg')
        case 'unionpay':
          return require('@/assets/cards/unionpay.svg')
        case 'visa':
          return require('@/assets/cards/visa.svg')
        default:
          return require('@/assets/cards/card.svg')
      }
    },
    async purchase() {
      try {
        this.disableCheckoutBtn = true

        const { data: setupIntentResponse } = await CheckoutService.setupIntent(
          {
            locationId: this.locationId,
            offerId: this.offerId,
          }
        )

        const {
          client_secret: clientSecret,
          payment_method: setupIntentPaymentMethod,
        } = setupIntentResponse

        if (!clientSecret) {
          throw new Error(
            'Client secret missing! Payment setup failed, try again!'
          )
        }

        let paymentMethod
        let isNewCard = true
        let stripeCustomerId

        if (this.chargeCardType === 'savedCards') {
          paymentMethod = this.selectedSavedCard
          isNewCard = false
          stripeCustomerId = this.contact.stripeCustomerId
        } else {
          if (setupIntentPaymentMethod) {
            paymentMethod = setupIntentPaymentMethod
          }

          const cardNumber = this.stripeElements.getElement('cardNumber')

          const result = await this.stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              type: 'card',
              card: cardNumber,
              billing_details: {
                name:
                  this.contact.full_name_lower_case || this.contact.first_name,
              },
            },
          })

          if (result.error) {
            throw new Error(result.error.message)
          }

          if (result.setupIntent) {
            paymentMethod = result.setupIntent.payment_method
          }
        }

        const payload = {
          fingerprint: this.contact.fingerprint,
          locationId: this.offer.locationId,
          productType: 'offer',
          paymentMethodId: paymentMethod,
          clientSecret,
          checkoutSource: this.checkoutSource,
          checkoutType: 'purchase',
          isNewCard: isNewCard,
          stripeCustomerId: stripeCustomerId,
        }

        try {
          const {
            data: { ct, previewUrl, upsellOfferId, featureFlag },
          } = await CheckoutService.checkout(this.offerId, payload)
          this.$emit('order-successful', {
            ct,
            previewUrl,
            upsellOfferId,
            featureFlag,
            closeModal: this.isInAppUpsell ? true : false,
          })
          this.disableCheckoutBtn = false
          // if (this.isInAppUpsell) {
          //   setTimeout(() => {
          //     window.close()
          //   }, 5000)
          // }
        } catch (err) {
          throw new Error(err?.response?.data?.msg)
        }
      } catch (error) {
        this.errMsg = error
        this.disableCheckoutBtn = false
      }
    },

    async purchaseV2(eventData) {
      try {
        this.disableCheckoutBtn = true
        const payload = {
          fingerprint: this.contact.fingerprint,
          locationId: this.offer.locationId,
          productType: 'offer',
          checkoutSource: this.checkoutSource,
          checkoutType: 'purchase',
          chargeId: eventData.orderId
            ? eventData.orderId
            : eventData.verifiedTrackingId,
          contactId: this.contact.id,
          offerId: this.offer.id,
          offerVersion: 'v2',
          appliedCoupon: this.localCouponData
            ? this.localCouponData.code
            : null,
        }

        try {
          const {
            data: { ct, previewUrl, upsellOfferId, featureFlag },
          } = await CheckoutService.checkout(this.offerId, payload)
          this.$emit('order-successful', {
            ct,
            previewUrl,
            upsellOfferId,
            featureFlag,
            closeModal: this.isInAppUpsell ? true : false,
          })
          this.disableCheckoutBtn = false
        } catch (err) {
          throw new Error(err?.response?.data?.msg)
        }
      } catch (error) {
        this.errMsg = error
        this.disableCheckoutBtn = false
      }
    },
    closeModal() {
      this.openModal = false
      this.$emit('close')
    },
    selectSavedCard(payload: string) {
      this.errMsg = ''
      this.selectedSavedCard = payload
    },
  },
  watch: {
    showModal(val: boolean) {
      this.openModal = val
    },
    couponData(newData: any) {
      this.localCouponData = newData
    },
  },
})
